import { z } from "zod";

// --- Schemas for all data types ---

export const OverheadBreakdownSchema = z.object({
  contextResend: z.number(),
  toolCalls: z.number(),
  thinking: z.number(),
  errorRecovery: z.number(),
  planning: z.number(),
  review: z.number(),
});

export type OverheadBreakdown = z.infer<typeof OverheadBreakdownSchema>;

// ── Projects ────────────────────────────────────────────────

export const ProjectSchema = z.object({
  id: z.string(),
  name: z.string(),
  client: z.string().optional(),
  claudeCodePath: z.string().optional(),
  createdAt: z.number(),
});

export type Project = z.infer<typeof ProjectSchema>;

// ── Estimates ───────────────────────────────────────────────

export const FeatureEstimateSchema = z.object({
  id: z.string(),
  name: z.string(),
  category: z.string(),
  complexity: z.string(),
  estimatedInputTokens: z.number(),
  estimatedOutputTokens: z.number(),
  estimatedCost: z.number(),
  recommendedModel: z.string(),
  taskTypes: z.array(z.string()),
  overheadBreakdown: OverheadBreakdownSchema,
});

export type FeatureEstimate = z.infer<typeof FeatureEstimateSchema>;

export const TotalEstimateSchema = z.object({
  tokens: z.number(),
  cost: z.number(),
  optimistic: z.number(),
  pessimistic: z.number(),
  allFrontier: z.number(),
});

export type TotalEstimate = z.infer<typeof TotalEstimateSchema>;

export const EstimateSchema = z.object({
  id: z.string(),
  projectId: z.string(),
  specHash: z.string(),
  features: z.array(FeatureEstimateSchema),
  totalEstimate: TotalEstimateSchema,
  calibrationSource: z.string(),
  createdAt: z.number(),
});

export type Estimate = z.infer<typeof EstimateSchema>;

// ── Actuals ─────────────────────────────────────────────────

export const ActualSchema = z.object({
  id: z.string(),
  projectId: z.string(),
  sessionId: z.string(),
  provider: z.string(),
  model: z.string(),
  inputTokens: z.number(),
  outputTokens: z.number(),
  cacheReadTokens: z.number(),
  cacheCreateTokens: z.number(),
  toolCalls: z.number(),
  filesTouched: z.array(z.string()).optional(),
  estimatedCost: z.number(),
  sessionStart: z.number(),
  sessionEnd: z.number().optional(),
  activeMinutes: z.number().optional(),
  featureId: z.string().optional(),
  tagConfidence: z.number().optional(),
  tagSource: z.string().optional(),
  tagStatus: z.string().optional(),
  timestamp: z.number(),
});

export type Actual = z.infer<typeof ActualSchema>;

// ── Intakes ─────────────────────────────────────────────────

export const VelocityBenchmarkRefSchema = z.object({
  avgSessions: z.number(),
  avgDays: z.number(),
  avgCost: z.number(),
  dataPoints: z.number(),
});

export const ExtractedSliceSchema = z.object({
  sliceId: z.string(),
  name: z.string(),
  category: z.string(),
  complexity: z.string(),
  priority: z.string(),
  description: z.string(),
  estimatedTokens: z.number(),
  estimatedCost: z.number(),
  velocityBenchmark: VelocityBenchmarkRefSchema.optional(),
  status: z.string(),
});

export type ExtractedSlice = z.infer<typeof ExtractedSliceSchema>;

export const IntakeSchema = z.object({
  id: z.string(),
  projectId: z.string(),
  rawInput: z.string(),
  source: z.string(),
  extractedSlices: z.array(ExtractedSliceSchema),
  createdAt: z.number(),
});

export type Intake = z.infer<typeof IntakeSchema>;

// ── Velocity Benchmarks ─────────────────────────────────────

export const VelocityBenchmarkSchema = z.object({
  id: z.string(),
  category: z.string(),
  complexity: z.string(),
  taskType: z.string().optional(),
  avgTokens: z.number(),
  avgCost: z.number(),
  avgSessions: z.number(),
  avgWallClockDays: z.number(),
  avgActiveMinutes: z.number(),
  p25Days: z.number(),
  p75Days: z.number(),
  dataPoints: z.number(),
  confidence: z.number(),
  lastUpdated: z.number(),
});

export type VelocityBenchmark = z.infer<typeof VelocityBenchmarkSchema>;

// ── Velocity Snapshots ──────────────────────────────────────

export const VelocitySnapshotSchema = z.object({
  id: z.string(),
  projectId: z.string().optional(),
  period: z.string(),
  featuresCompleted: z.number(),
  totalTokens: z.number(),
  totalCost: z.number(),
  avgEstimateAccuracy: z.number(),
  avgCostPerFeature: z.number(),
  avgDaysPerFeature: z.number(),
  cacheHitRate: z.number(),
  autoTagRate: z.number(),
  createdAt: z.number(),
});

export type VelocitySnapshot = z.infer<typeof VelocitySnapshotSchema>;

// ── Reconciliations ─────────────────────────────────────────

export const TagQualitySchema = z.object({
  autoTagged: z.number(),
  likelyTagged: z.number(),
  manualTagged: z.number(),
});

export const FeatureDeltaSchema = z.object({
  featureId: z.string(),
  featureName: z.string(),
  estimatedTokens: z.number(),
  actualTokens: z.number(),
  delta: z.number(),
  accuracy: z.number(),
  modelRecommended: z.string(),
  modelUsed: z.string(),
  sessionsCount: z.number(),
  wallClockDays: z.number().optional(),
  activeMinutes: z.number().optional(),
  tagQuality: TagQualitySchema,
  insights: z.array(z.string()),
});

export type FeatureDelta = z.infer<typeof FeatureDeltaSchema>;

export const RegistryImprovementSchema = z.object({
  featureId: z.string(),
  field: z.string(),
  added: z.array(z.string()),
});

export const SuggestedAdjustmentSchema = z.object({
  profileKey: z.string(),
  currentValue: z.number(),
  suggestedValue: z.number(),
  dataPoints: z.number(),
  confidence: z.number(),
});

export type SuggestedAdjustment = z.infer<typeof SuggestedAdjustmentSchema>;

export const ReconciliationSchema = z.object({
  id: z.string(),
  projectId: z.string(),
  estimateId: z.string(),
  featureDeltas: z.array(FeatureDeltaSchema),
  overallAccuracy: z.number(),
  autoTagAccuracy: z.number(),
  registryImprovements: z
    .array(RegistryImprovementSchema)
    .optional(),
  suggestedAdjustments: z.array(SuggestedAdjustmentSchema),
  createdAt: z.number(),
});

export type Reconciliation = z.infer<typeof ReconciliationSchema>;

// ── Calibrated Profiles ─────────────────────────────────────

export const CalibratedProfileSchema = z.object({
  id: z.string(),
  taskType: z.string(),
  baseInput: z.number(),
  baseOutput: z.number(),
  overheadMultipliers: OverheadBreakdownSchema,
  dataPoints: z.number(),
  confidence: z.number(),
  lastUpdated: z.number(),
});

export type CalibratedProfile = z.infer<typeof CalibratedProfileSchema>;

// ── Model Pricing ───────────────────────────────────────────

export const ModelPricingSchema = z.object({
  id: z.string(),
  model: z.string(),
  provider: z.string(),
  inputPerMTok: z.number(),
  outputPerMTok: z.number(),
  cacheReadPerMTok: z.number(),
  batchDiscount: z.number(),
  lastVerified: z.number(),
});

export type ModelPricing = z.infer<typeof ModelPricingSchema>;

// ── Store Interface ─────────────────────────────────────────

export interface FathomStore {
  // Projects
  getProject(name: string): Promise<Project | null>;
  upsertProject(project: Omit<Project, "id">): Promise<string>;

  // Estimates
  saveEstimate(estimate: Omit<Estimate, "id">): Promise<string>;
  getEstimates(projectId: string): Promise<Estimate[]>;

  // Actuals
  saveActual(actual: Omit<Actual, "id">): Promise<string>;
  getActuals(
    projectId: string,
    opts?: { featureId?: string },
  ): Promise<Actual[]>;

  // Intakes
  saveIntake(intake: Omit<Intake, "id">): Promise<string>;
  getIntakes(projectId: string): Promise<Intake[]>;

  // Velocity
  saveBenchmark(benchmark: Omit<VelocityBenchmark, "id">): Promise<string>;
  getBenchmarks(opts?: {
    category?: string;
    complexity?: string;
  }): Promise<VelocityBenchmark[]>;
  saveSnapshot(snapshot: Omit<VelocitySnapshot, "id">): Promise<string>;
  getSnapshots(opts?: {
    projectId?: string;
    period?: string;
  }): Promise<VelocitySnapshot[]>;

  // Reconciliation
  saveReconciliation(
    recon: Omit<Reconciliation, "id">,
  ): Promise<string>;
  getReconciliations(projectId: string): Promise<Reconciliation[]>;

  // Calibration
  saveProfile(profile: Omit<CalibratedProfile, "id">): Promise<string>;
  getProfiles(): Promise<CalibratedProfile[]>;
  getProfile(taskType: string): Promise<CalibratedProfile | null>;

  // Pricing
  savePricing(pricing: Omit<ModelPricing, "id">): Promise<string>;
  getPricing(model?: string): Promise<ModelPricing[]>;
}

// ── Store Config ────────────────────────────────────────────

export type StoreConfig =
  | { type: "file"; path: string }
  | { type: "convex"; url: string };
